import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, LogOut, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import {
  ANALYTICS_PATH,
  AVAILABILITY_SETTINGS_PATH,
  BUSINESS_SETTINGS_PATH,
  CALENDAR_PATH,
  CLIENTS_PATH,
  DASHBOARD_PATH,
  LOGIN_PATH,
  ONBOARDING_PATH,
  SERVICES_SETTINGS_PATH,
  SPACES_SETTINGS_PATH,
  STAFF_SETTINGS_PATH,
} from './auth-routing';
import { useAuth } from './auth-context';
import { currentUserRequest } from '../../lib/api/auth-client';
import { listBusinessStaffRequest } from '../../lib/api/business-client';
import type { components } from '../../lib/api/openapi-schema';
import { Button } from '../../components/ui/button';
import { resolveSessionBusinessUserType } from './session';

type BusinessState = 'any' | 'has-business' | 'missing-business';

interface ProtectedRouteProps {
  children: ReactElement;
  businessState?: BusinessState;
}

type BusinessAccessState =
  | { status: 'idle' | 'allowed' | 'loading' }
  | { status: 'blocked'; message: string; kind?: 'deactivated' | 'forbidden' }
  | { status: 'error'; message: string };

function normalizeEmail(value: string | null | undefined) {
  return value?.trim().toLowerCase() || null;
}

type StaffPermissions = components['schemas']['StaffPermissions'];

function resolveRoutePermission(
  pathname: string,
  permissions: StaffPermissions
): 'none' | 'allowed' {
  const rules: Array<[string, keyof StaffPermissions]> = [
    [DASHBOARD_PATH, 'dashboard'],
    [ANALYTICS_PATH, 'dashboard'],
    [CALENDAR_PATH, 'appointments'],
    [CLIENTS_PATH, 'clients'],
    [STAFF_SETTINGS_PATH, 'staff'],
    [SERVICES_SETTINGS_PATH, 'services'],
    [SPACES_SETTINGS_PATH, 'spaces'],
    [AVAILABILITY_SETTINGS_PATH, 'availabilities'],
    [BUSINESS_SETTINGS_PATH, 'business'],
  ];

  for (const [path, resource] of rules) {
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      const perm = permissions[resource] as { read: string };
      return perm.read === 'none' ? 'none' : 'allowed';
    }
  }

  return 'allowed';
}

function normalizeUserType(value: unknown): components['schemas']['UserType'] | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'owner') {
    return 'Owner';
  }

  if (normalized === 'staff') {
    return 'Staff';
  }

  return undefined;
}

function LoadingScreen({ message = 'A carregar...' }: { message?: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background">
      <div className="inline-flex items-center gap-3 rounded-full border border-border bg-white px-5 py-3 text-sm font-medium text-muted-foreground shadow-field">
        <Loader2 className="h-4 w-4 animate-spin" />
        {message}
      </div>
    </main>
  );
}

function StaffAccessDialog({
  message,
  onGoBack,
  onLogout,
  onRetry,
  title = 'Conta desativada',
}: {
  message: string;
  onGoBack?: () => void;
  onLogout: () => void;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f7efe5] px-4 py-8">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-access-title"
        className="w-full max-w-lg rounded-[2rem] border border-white/70 bg-[#fffaf4] px-6 py-6 shadow-[0_36px_80px_rgba(22,50,59,0.18)] sm:px-8"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.1rem] bg-[#fff0ea] text-[#c25e3f]">
            <AlertCircle className="h-6 w-6" />
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a5d2e]">
              Acesso ao negócio
            </p>
            <h1
              id="staff-access-title"
              className="font-serif text-3xl font-semibold text-[#16323b]"
            >
              {title}
            </h1>
            <p className="text-sm leading-6 text-[#6b736d]">{message}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-[#efe2d3] pt-5 sm:flex-row sm:justify-end">
          {onRetry ? (
            <Button
              type="button"
              variant="outline"
              className="h-12 gap-2 rounded-[1.2rem] border-[#dfd4c6] bg-white text-[#16323b]"
              onClick={onRetry}
            >
              <RotateCcw className="h-4 w-4" />
              Tentar novamente
            </Button>
          ) : null}
          {onGoBack ? (
            <Button
              type="button"
              className="h-12 gap-2 rounded-[1.2rem] bg-[#16323b] text-white hover:bg-[#0f252d]"
              onClick={onGoBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          ) : (
            <Button
              type="button"
              className="h-12 gap-2 rounded-[1.2rem] bg-[#16323b] text-white hover:bg-[#0f252d]"
              onClick={onLogout}
            >
              <LogOut className="h-4 w-4" />
              Terminar sessão
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}

export function ProtectedRoute({
  children,
  businessState = 'any',
}: ProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isHydrating, logout, session } = useAuth();
  const [accessState, setAccessState] = useState<BusinessAccessState>({
    status: 'idle',
  });
  const [retryNonce, setRetryNonce] = useState(0);
  const businessId =
    session?.businessSnapshot?.id ?? session?.businessReference?.id ?? null;
  const shouldVerifyBusinessAccess =
    businessState === 'has-business' &&
    isAuthenticated &&
    Boolean(session?.hasBusiness && session.token && businessId);
  const storedUserType = resolveSessionBusinessUserType(session);
  const storedEmail = session?.email;

  useEffect(() => {
    let cancelled = false;

    async function verifyBusinessAccess() {
      if (!shouldVerifyBusinessAccess || !session?.token || !businessId) {
        setAccessState({ status: 'idle' });
        return;
      }

      if (storedUserType === 'Owner') {
        setAccessState({ status: 'allowed' });
        return;
      }

      setAccessState({ status: 'loading' });

      try {
        let userType: components['schemas']['UserType'] | undefined =
          normalizeUserType(storedUserType);
        let userEmail = storedEmail;

        if (!userType) {
          const user = await currentUserRequest(session.token);
          userType = normalizeUserType(user.user_type);
          userEmail = user.email || userEmail;
        }

        if (cancelled) {
          return;
        }

        if (userType !== 'Staff') {
          setAccessState({ status: 'allowed' });
          return;
        }

        const staffResponse = await listBusinessStaffRequest(session.token, businessId, {
          email: userEmail,
          status: 'all',
          limit: 1,
        });
        const normalizedUserEmail = normalizeEmail(userEmail);
        const staffOptions = staffResponse.data ?? [];
        const currentStaff =
          staffOptions.find(
            (staff) => normalizeEmail(staff.email) === normalizedUserEmail
          ) ??
          staffOptions[0] ??
          null;

        if (cancelled) {
          return;
        }

        if (!currentStaff || currentStaff.archived_at) {
          setAccessState({
            status: 'blocked',
            message:
              'A sua conta foi desativada para este negócio. Ainda pode entrar na sua conta, mas não pode gerir marcações, clientes ou definições enquanto o proprietário não reativar o seu acesso.',
          });
          return;
        }

        const routeAccess = resolveRoutePermission(location.pathname, currentStaff.permissions);
        if (routeAccess === 'none') {
          setAccessState({
            status: 'blocked',
            kind: 'forbidden',
            message: 'Não tem permissão para aceder a esta página.',
          });
          return;
        }

        setAccessState({ status: 'allowed' });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setAccessState({
          status: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Não foi possível validar o acesso ao negócio agora.',
        });
      }
    }

    verifyBusinessAccess();

    return () => {
      cancelled = true;
    };
  }, [
    businessId,
    location.pathname,
    retryNonce,
    session?.token,
    shouldVerifyBusinessAccess,
    storedEmail,
    storedUserType,
  ]);

  const retryAccessCheck = useCallback(() => {
    setRetryNonce((currentValue) => currentValue + 1);
  }, []);

  if (isHydrating) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location.pathname }} />;
  }

  if (businessState === 'has-business' && !session?.hasBusiness) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  if (businessState === 'missing-business' && session?.hasBusiness) {
    return <Navigate to={DASHBOARD_PATH} replace />;
  }

  if (
    shouldVerifyBusinessAccess &&
    (accessState.status === 'idle' || accessState.status === 'loading')
  ) {
    return <LoadingScreen message="A validar acesso ao negócio..." />;
  }

  if (shouldVerifyBusinessAccess && accessState.status === 'blocked') {
    const isForbidden = accessState.kind === 'forbidden';
    return (
      <StaffAccessDialog
        title={isForbidden ? 'Acesso restrito' : 'Conta desativada'}
        message={accessState.message}
        onLogout={logout}
        onGoBack={isForbidden ? () => navigate(-1) : undefined}
      />
    );
  }

  if (shouldVerifyBusinessAccess && accessState.status === 'error') {
    return (
      <StaffAccessDialog
        message={accessState.message}
        onLogout={logout}
        onRetry={retryAccessCheck}
      />
    );
  }

  return children;
}
